import { App } from '@web/query/App';
import React, { PropsWithChildren } from 'react';

interface FeatureCondition {
  features: PlatformFeatures[];
  invert?: boolean;
}

interface PriorityRule {
  condition: FeatureCondition;
  component: React.ReactNode;
  priority: number; // Lower number = higher priority
}

interface RenderWithFeaturePriorityProps extends PropsWithChildren {
  rules: PriorityRule[];
}

function RenderWithFeaturePriority({ rules }: RenderWithFeaturePriorityProps) {
  const { data } = App.useFeatures();

  // Sort rules by priority (ascending)
  const sortedRules = React.useMemo(() => {
    return [...rules].sort((a, b) => a.priority - b.priority);
  }, [rules]);

  // Check which rule matches first
  const matchingRule = React.useMemo(() => {
    for (const rule of sortedRules) {
      const { features, invert } = rule.condition;
      /**
       * If invert is true, it checks if all features are not enabled
       */
      const allFeaturesMatch = features.every((f) => (invert ? !data?.[f] : data?.[f]));

      if (allFeaturesMatch) {
        return rule;
      }
    }
    return null;
  }, [sortedRules, data]);

  if (matchingRule) {
    return <>{matchingRule.component}</>;
  }

  return null;
}

export { RenderWithFeaturePriority };
